import { Client } from '@lemonade-hq/nuiza';
import { devWarn } from '@lemonade-hq/ts-helpers';
import { appendQueryParams, createTrackingHeaders } from './utils';
import type { BaseClientData } from './utils';

export interface ConversationMessage {
  readonly id: string;
  readonly generatedBy: 'llm' | 'script' | 'user';
  readonly side: string;
  readonly content?: Record<string, unknown> | string;
  readonly replyToMessageId?: string;
  readonly presentation?: {
    readonly type: 'uniclient';
    readonly payload: unknown;
  };
}

export interface StartConversationRequest {
  readonly experience: string;
  readonly channel: string;
  readonly context?: Record<string, unknown>;
  readonly forceRestart?: boolean;
}

export interface StartConversationResponse {
  readonly conversationId: string;
  readonly messages: ConversationMessage[];
}

export interface SendMessageRequest {
  readonly conversationId: string;
  readonly input: Record<string, unknown>;
  readonly replyToMessageId?: string;
}

export interface SendMessageResponse {
  readonly messages: ConversationMessage[];
}

interface ConversationRouterClientData extends BaseClientData {}

export interface SessionMetadata {
  readonly schema: {
    readonly validations: { readonly looseInput: boolean; readonly type: string }[];
    readonly schema: Record<string, { readonly type: string }>;
    readonly type: string;
  };
  readonly assets: string[];
  readonly auth: {
    readonly userPublicId: string | null;
    readonly visitorId: string;
  };
  readonly createdAt: string;
}

export class ConversationRouterClient {
  constructor(private readonly clientData: ConversationRouterClientData) {}

  async startConversation(request: StartConversationRequest): Promise<StartConversationResponse> {
    devWarn(`🚀 ConversationRouterClient.startConversation called with: ${JSON.stringify(request)}`);

    const { serverBaseUrl, accessTokenOverride, emailCode } = this.clientData;

    const trackingHeaders = createTrackingHeaders();

    const path = new URL(`${serverBaseUrl}/api/v1/conversations/start`);

    try {
      const data = await Client.post<StartConversationResponse>(
        appendQueryParams(path, accessTokenOverride, emailCode),
        request as unknown as Record<string, unknown>,
        { headers: trackingHeaders },
      );

      devWarn(`✅ ConversationRouterClient.startConversation returning: ${JSON.stringify(data)}`);
      return data;
    } catch (error) {
      devWarn(`❌ ConversationRouterClient.startConversation error: ${String(error)}`);
      throw error;
    }
  }

  async sendMessage(request: SendMessageRequest): Promise<SendMessageResponse> {
    devWarn(`📤 ConversationRouterClient.sendMessage called with: ${JSON.stringify(request)}`);

    const { serverBaseUrl, accessTokenOverride, emailCode } = this.clientData;

    const trackingHeaders = createTrackingHeaders();

    const path = new URL(`${serverBaseUrl}/api/v1/conversations/${request.conversationId}/messages`);

    try {
      const data = await Client.post<SendMessageResponse>(
        appendQueryParams(path, accessTokenOverride, emailCode),
        {
          input: request.input,
          replyToMessageId: request.replyToMessageId,
        },
        { headers: trackingHeaders },
      );

      devWarn(`✅ ConversationRouterClient.sendMessage returning: ${JSON.stringify(data)}`);
      return data;
    } catch (error) {
      devWarn(`❌ ConversationRouterClient.sendMessage error: ${String(error)}`);
      throw error;
    }
  }
}
